import React, { useEffect } from "react";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";
import "./Logistics.css";

import {
    FiArrowRight,
    FiTruck,
    FiMapPin,
    FiPackage,
    FiMessageSquare,
    FiFileText,
    FiSearch,
    FiZap,
    FiCheckCircle,
    FiBarChart2,
    FiRefreshCw,
    FiClock,
    FiLayers,
    FiTarget,
    FiUsers,
    FiNavigation,
    FiBox,
    FiPhoneCall,
} from "react-icons/fi";

const Logistics = () => {
    useEffect(() => {
        document.title = "AI for Logistics | Dropgear IO";
    }, []);

    return (
        <>
            <Header />

            <main className="logistics-page">

                {/* =====================================================
            HERO
        ===================================================== */}

                <section className="logistics-hero">

                    <div className="logistics-hero-grid">

                        <div className="logistics-hero-content">

                            <span className="logistics-eyebrow">
                                LOGISTICS & SUPPLY CHAIN
                            </span>

                            <h1>
                                AI for
                                <span> logistics operations.</span>
                            </h1>

                            <p>
                                Dropgear IO helps logistics teams bring AI into
                                everyday operational workflows — from shipment
                                coordination and customer communication to
                                exception handling, documentation, and
                                operational intelligence.
                            </p>

                            <div className="logistics-hero-actions">

                                <a
                                    href="/book-demo"
                                    className="logistics-primary-btn"
                                >
                                    Book a Demo
                                    <FiArrowRight />
                                </a>

                                <a
                                    href="#logistics-solutions"
                                    className="logistics-secondary-btn"
                                >
                                    Explore Solutions
                                </a>

                            </div>

                            <div className="logistics-hero-note">

                                <FiCheckCircle />

                                <span>
                                    Built around practical logistics workflows
                                </span>

                            </div>

                        </div>


                        {/* DASHBOARD */}

                        <div className="logistics-hero-visual">

                            <div className="logistics-visual-glow"></div>

                            <div className="logistics-dashboard">

                                <div className="logistics-dashboard-header">

                                    <div>

                                        <span>
                                            LOGISTICS OPERATIONS
                                        </span>

                                        <strong>
                                            AI Control Workspace
                                        </strong>

                                    </div>

                                    <div className="logistics-live-status">
                                        <i></i>
                                        Live
                                    </div>

                                </div>


                                {/* METRICS */}

                                <div className="logistics-metrics">

                                    <div className="logistics-metric-card">

                                        <span>
                                            SHIPMENTS
                                        </span>

                                        <strong>
                                            1,284
                                        </strong>

                                        <small>
                                            Active
                                        </small>

                                    </div>

                                    <div className="logistics-metric-card">

                                        <span>
                                            IN TRANSIT
                                        </span>

                                        <strong>
                                            842
                                        </strong>

                                        <small>
                                            Moving
                                        </small>

                                    </div>

                                    <div className="logistics-metric-card">

                                        <span>
                                            EXCEPTIONS
                                        </span>

                                        <strong>
                                            27
                                        </strong>

                                        <small>
                                            Requires attention
                                        </small>

                                    </div>

                                </div>


                                {/* TRACKING */}

                                <div className="logistics-tracking">

                                    <div className="logistics-tracking-title">

                                        <span>
                                            AI SHIPMENT MONITOR
                                        </span>

                                        <FiNavigation />

                                    </div>


                                    <div className="logistics-route">

                                        <div className="logistics-route-point completed">

                                            <span></span>

                                            <div>

                                                <strong>
                                                    Warehouse
                                                </strong>

                                                <small>
                                                    Dispatched
                                                </small>

                                            </div>

                                        </div>


                                        <div className="logistics-route-line">
                                            <div></div>
                                        </div>


                                        <div className="logistics-route-point completed">

                                            <span></span>

                                            <div>

                                                <strong>
                                                    Transit Hub
                                                </strong>

                                                <small>
                                                    Processed
                                                </small>

                                            </div>

                                        </div>


                                        <div className="logistics-route-line active">
                                            <div></div>
                                        </div>


                                        <div className="logistics-route-point active">

                                            <span></span>

                                            <div>

                                                <strong>
                                                    Destination
                                                </strong>

                                                <small>
                                                    ETA monitored
                                                </small>

                                            </div>

                                        </div>

                                    </div>


                                    <div className="logistics-ai-alert">

                                        <div className="logistics-ai-alert-icon">
                                            <FiZap />
                                        </div>

                                        <div>

                                            <strong>
                                                AI workflow detected
                                            </strong>

                                            <span>
                                                Shipment update requires attention
                                            </span>

                                        </div>

                                        <FiArrowRight />

                                    </div>

                                </div>

                            </div>


                            <div className="logistics-floating-card logistics-float-one">

                                <FiTruck />

                                <span>
                                    Route update received
                                </span>

                            </div>


                            <div className="logistics-floating-card logistics-float-two">

                                <FiPackage />

                                <span>
                                    Shipment workflow updated
                                </span>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            INTRO
        ===================================================== */}

                <section className="logistics-intro">

                    <div className="logistics-container">

                        <div className="logistics-intro-grid">

                            <div>

                                <span className="logistics-section-label">
                                    THE LOGISTICS WORKFLOW
                                </span>

                                <h2>
                                    Logistics moves
                                    <strong> through constant change.</strong>
                                </h2>

                            </div>


                            <div>

                                <p>
                                    Shipments move across locations, teams,
                                    systems, and time zones. Every movement creates
                                    information that needs to be processed,
                                    communicated, and acted upon.
                                </p>

                                <p>
                                    Dropgear IO helps logistics organizations use AI
                                    to support these workflows, connect operational
                                    information, and reduce repetitive work.
                                </p>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            SOLUTIONS
        ===================================================== */}

                <section
                    id="logistics-solutions"
                    className="logistics-solutions"
                >

                    <div className="logistics-container">

                        <div className="logistics-section-heading">

                            <span>
                                AI FOR LOGISTICS
                            </span>

                            <h2>
                                Turn operational
                                <br />
                                complexity into
                                <strong> intelligent workflows.</strong>
                            </h2>

                            <p>
                                Apply AI to the workflows that keep shipments,
                                teams, customers, and operational information
                                moving together.
                            </p>

                        </div>


                        <div className="logistics-solutions-grid">

                            {/* 01 */}

                            <div className="logistics-solution-card featured">

                                <div className="logistics-solution-icon">
                                    <FiTruck />
                                </div>

                                <span>
                                    01
                                </span>

                                <h3>
                                    Shipment Coordination
                                </h3>

                                <p>
                                    Support teams in coordinating shipment-related
                                    activities across defined operational workflows.
                                </p>

                                <ul>

                                    <li>
                                        <FiCheckCircle />
                                        Shipment updates
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Workflow coordination
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Operational task support
                                    </li>

                                </ul>

                            </div>


                            {/* 02 */}

                            <div className="logistics-solution-card">

                                <div className="logistics-solution-icon">
                                    <FiNavigation />
                                </div>

                                <span>
                                    02
                                </span>

                                <h3>
                                    Route & Dispatch Assistance
                                </h3>

                                <p>
                                    Help operations teams process route,
                                    dispatch, and delivery-related information.
                                </p>

                                <ul>

                                    <li>
                                        <FiCheckCircle />
                                        Dispatch information
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Route workflow support
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Delivery coordination
                                    </li>

                                </ul>

                            </div>


                            {/* 03 */}

                            <div className="logistics-solution-card">

                                <div className="logistics-solution-icon">
                                    <FiMessageSquare />
                                </div>

                                <span>
                                    03
                                </span>

                                <h3>
                                    Customer Communication
                                </h3>

                                <p>
                                    Support routine shipment and delivery
                                    communication through AI-assisted workflows.
                                </p>

                                <ul>

                                    <li>
                                        <FiCheckCircle />
                                        Shipment questions
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Delivery information
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Communication assistance
                                    </li>

                                </ul>

                            </div>


                            {/* 04 */}

                            <div className="logistics-solution-card">

                                <div className="logistics-solution-icon">
                                    <FiZap />
                                </div>

                                <span>
                                    04
                                </span>

                                <h3>
                                    Exception Handling
                                </h3>

                                <p>
                                    Help teams identify and organize operational
                                    exceptions that require attention or follow-up.
                                </p>

                                <ul>

                                    <li>
                                        <FiCheckCircle />
                                        Exception identification
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Task routing
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Follow-up assistance
                                    </li>

                                </ul>

                            </div>


                            {/* 05 */}

                            <div className="logistics-solution-card">

                                <div className="logistics-solution-icon">
                                    <FiFileText />
                                </div>

                                <span>
                                    05
                                </span>

                                <h3>
                                    Documentation Workflows
                                </h3>

                                <p>
                                    Support repetitive documentation and
                                    information-processing tasks across logistics
                                    operations.
                                </p>

                                <ul>

                                    <li>
                                        <FiCheckCircle />
                                        Document organization
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Information extraction
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Workflow assistance
                                    </li>

                                </ul>

                            </div>


                            {/* 06 */}

                            <div className="logistics-solution-card">

                                <div className="logistics-solution-icon">
                                    <FiBarChart2 />
                                </div>

                                <span>
                                    06
                                </span>

                                <h3>
                                    Logistics Intelligence
                                </h3>

                                <p>
                                    Help teams explore operational information
                                    and understand recurring logistics patterns.
                                </p>

                                <ul>

                                    <li>
                                        <FiCheckCircle />
                                        Operational insights
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Workflow analysis
                                    </li>

                                    <li>
                                        <FiCheckCircle />
                                        Performance exploration
                                    </li>

                                </ul>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            SHIPMENT EXPERIENCE
        ===================================================== */}

                <section className="logistics-shipment">

                    <div className="logistics-container">

                        <div className="logistics-two-column">

                            {/* VISUAL */}

                            <div className="logistics-feature-visual">

                                <div className="logistics-tracking-card">

                                    <div className="logistics-tracking-card-header">

                                        <div>

                                            <span>
                                                SHIPMENT
                                            </span>

                                            <strong>
                                                DG-48291
                                            </strong>

                                        </div>

                                        <span className="logistics-status">
                                            In Transit
                                        </span>

                                    </div>


                                    <div className="logistics-map">

                                        <div className="logistics-map-grid"></div>

                                        <div className="logistics-map-route">

                                            <span className="start"></span>

                                            <div className="route-path">
                                                <i></i>
                                                <i></i>
                                                <i></i>
                                            </div>

                                            <span className="destination"></span>

                                        </div>

                                        <div className="logistics-map-truck">
                                            <FiTruck />
                                        </div>

                                    </div>


                                    <div className="logistics-shipment-details">

                                        <div>

                                            <span>
                                                ORIGIN
                                            </span>

                                            <strong>
                                                Mumbai
                                            </strong>

                                        </div>

                                        <FiArrowRight />

                                        <div>

                                            <span>
                                                DESTINATION
                                            </span>

                                            <strong>
                                                Pune
                                            </strong>

                                        </div>

                                    </div>


                                    <div className="logistics-shipment-progress">

                                        <div>

                                            <span>
                                                Shipment progress
                                            </span>

                                            <strong>
                                                72%
                                            </strong>

                                        </div>

                                        <div className="logistics-progress-bar">
                                            <span></span>
                                        </div>

                                    </div>

                                </div>

                            </div>


                            {/* CONTENT */}

                            <div className="logistics-feature-content">

                                <span className="logistics-section-label">
                                    SHIPMENT INTELLIGENCE
                                </span>

                                <h2>
                                    Keep teams
                                    <strong> closer to every shipment.</strong>
                                </h2>

                                <p>
                                    Logistics teams often need to combine information
                                    from multiple operational sources. AI can help
                                    summarize information, surface relevant updates,
                                    and support the next step in a workflow.
                                </p>


                                <div className="logistics-check-list">

                                    <div>

                                        <FiCheckCircle />

                                        <span>
                                            Shipment information assistance
                                        </span>

                                    </div>

                                    <div>

                                        <FiCheckCircle />

                                        <span>
                                            Delivery status workflows
                                        </span>

                                    </div>

                                    <div>

                                        <FiCheckCircle />

                                        <span>
                                            Exception follow-up support
                                        </span>

                                    </div>

                                    <div>

                                        <FiCheckCircle />

                                        <span>
                                            Operational communication
                                        </span>

                                    </div>

                                </div>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            OPERATIONS
        ===================================================== */}

                <section className="logistics-operations">

                    <div className="logistics-container">

                        <div className="logistics-section-heading centered">

                            <span>
                                OPERATIONAL WORKFLOWS
                            </span>

                            <h2>
                                Less repetitive work.
                                <br />
                                <strong>More operational clarity.</strong>
                            </h2>

                            <p>
                                Use AI to support the repetitive information
                                processing and coordination tasks that consume
                                valuable operational time.
                            </p>

                        </div>


                        <div className="logistics-operations-grid">

                            <div className="logistics-operation-card">

                                <div className="logistics-operation-icon">
                                    <FiPackage />
                                </div>

                                <h3>
                                    Order Processing
                                </h3>

                                <p>
                                    Assist teams in organizing order and shipment
                                    information across defined workflows.
                                </p>

                            </div>


                            <div className="logistics-operation-card">

                                <div className="logistics-operation-icon">
                                    <FiClock />
                                </div>

                                <h3>
                                    Delivery Coordination
                                </h3>

                                <p>
                                    Support teams with delivery-related updates,
                                    requests, and operational coordination.
                                </p>

                            </div>


                            <div className="logistics-operation-card">

                                <div className="logistics-operation-icon">
                                    <FiLayers />
                                </div>

                                <h3>
                                    Process Automation
                                </h3>

                                <p>
                                    Connect repetitive operational steps with
                                    AI-assisted automation.
                                </p>

                            </div>


                            <div className="logistics-operation-card">

                                <div className="logistics-operation-icon">
                                    <FiSearch />
                                </div>

                                <h3>
                                    Information Retrieval
                                </h3>

                                <p>
                                    Help teams quickly find relevant information
                                    within connected operational sources.
                                </p>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            USE CASES
        ===================================================== */}

                <section className="logistics-usecases">

                    <div className="logistics-container">

                        <div className="logistics-section-heading">

                            <span>
                                PRACTICAL USE CASES
                            </span>

                            <h2>
                                Start with one workflow.
                                <br />
                                <strong>Expand as you learn.</strong>
                            </h2>

                            <p>
                                Begin with a clearly defined operational challenge
                                and build an AI workflow around the way your team
                                already works.
                            </p>

                        </div>


                        <div className="logistics-usecase-list">

                            <div className="logistics-usecase">

                                <span>
                                    01
                                </span>

                                <FiTruck />

                                <div>

                                    <h3>
                                        Shipment Operations Assistant
                                    </h3>

                                    <p>
                                        Help teams process shipment information,
                                        updates, and operational requests.
                                    </p>

                                </div>

                                <FiArrowRight />

                            </div>


                            <div className="logistics-usecase">

                                <span>
                                    02
                                </span>

                                <FiMessageSquare />

                                <div>

                                    <h3>
                                        Delivery Communication Assistant
                                    </h3>

                                    <p>
                                        Support routine customer and delivery-related
                                        communication workflows.
                                    </p>

                                </div>

                                <FiArrowRight />

                            </div>


                            <div className="logistics-usecase">

                                <span>
                                    03
                                </span>

                                <FiZap />

                                <div>

                                    <h3>
                                        Exception Management Assistant
                                    </h3>

                                    <p>
                                        Organize exceptions and help route follow-up
                                        tasks to the appropriate operational workflow.
                                    </p>

                                </div>

                                <FiArrowRight />

                            </div>


                            <div className="logistics-usecase">

                                <span>
                                    04
                                </span>

                                <FiBarChart2 />

                                <div>

                                    <h3>
                                        Logistics Intelligence Assistant
                                    </h3>

                                    <p>
                                        Help teams explore operational information
                                        and investigate recurring process patterns.
                                    </p>

                                </div>

                                <FiArrowRight />

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            AI + PEOPLE
        ===================================================== */}

                <section className="logistics-human">

                    <div className="logistics-container">

                        <div className="logistics-human-grid">

                            <div>

                                <span className="logistics-section-label">
                                    AI + OPERATIONS TEAMS
                                </span>

                                <h2>
                                    AI handles the
                                    <strong> repetitive layer.</strong>
                                </h2>

                                <p>
                                    Logistics depends on people making decisions,
                                    coordinating teams, and responding to changing
                                    conditions. Dropgear IO focuses AI on the
                                    repetitive information and workflow layer so
                                    teams can focus on higher-value operational work.
                                </p>

                            </div>


                            <div className="logistics-principles">

                                <div>

                                    <FiRefreshCw />

                                    <section>

                                        <h3>
                                            Continuous workflow support
                                        </h3>

                                        <p>
                                            Assist teams across recurring operational
                                            processes and information flows.
                                        </p>

                                    </section>

                                </div>


                                <div>

                                    <FiUsers />

                                    <section>

                                        <h3>
                                            People stay in control
                                        </h3>

                                        <p>
                                            Keep operational judgment and important
                                            decisions with the appropriate teams.
                                        </p>

                                    </section>

                                </div>


                                <div>

                                    <FiTarget />

                                    <section>

                                        <h3>
                                            Built around outcomes
                                        </h3>

                                        <p>
                                            Focus AI implementation on measurable
                                            workflow improvements and useful outcomes.
                                        </p>

                                    </section>

                                </div>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            WHY DROPGEAR
        ===================================================== */}

                <section className="logistics-why">

                    <div className="logistics-container">

                        <div className="logistics-why-grid">

                            <div>

                                <span className="logistics-section-label">
                                    WHY DROPGEAR IO
                                </span>

                                <h2>
                                    AI designed around
                                    <strong> logistics work.</strong>
                                </h2>

                                <p>
                                    Logistics organizations need systems that
                                    understand workflows, information, and
                                    operational context — not just generic AI
                                    interfaces.
                                </p>

                                <a
                                    href="/contact"
                                    className="logistics-primary-btn"
                                >
                                    Discuss Your Workflow
                                    <FiArrowRight />
                                </a>

                            </div>


                            <div className="logistics-why-list">

                                <div>

                                    <FiTarget />

                                    <section>

                                        <h3>
                                            Workflow-first
                                        </h3>

                                        <p>
                                            Start with the operational problem instead
                                            of starting with the technology.
                                        </p>

                                    </section>

                                </div>


                                <div>

                                    <FiLayers />

                                    <section>

                                        <h3>
                                            Connected AI systems
                                        </h3>

                                        <p>
                                            Combine AI models, automation, agents,
                                            information, and business rules where useful.
                                        </p>

                                    </section>

                                </div>


                                <div>

                                    <FiZap />

                                    <section>

                                        <h3>
                                            Practical automation
                                        </h3>

                                        <p>
                                            Automate repetitive processes while keeping
                                            important operational decisions with people.
                                        </p>

                                    </section>

                                </div>

                            </div>

                        </div>

                    </div>

                </section>


                {/* =====================================================
            CTA
        ===================================================== */}

                <section className="logistics-final-cta">

                    <div className="logistics-final-glow"></div>

                    <div className="logistics-final-content">

                        <span>
                            LOGISTICS & SUPPLY CHAIN
                        </span>

                        <h2>
                            Ready to make your
                            <br />
                            logistics workflows smarter?
                        </h2>

                        <p>
                            Tell Dropgear IO about a logistics workflow,
                            operational challenge, or repetitive process
                            you want to improve with AI.
                        </p>

                        <a
                            href="/contact"
                            className="logistics-cta-button"
                        >
                            Start a Conversation
                            <FiArrowRight />
                        </a>

                    </div>

                </section>

            </main>

            <Footer />
        </>
    );
};

export default Logistics;